import React from "react";

import { faCheck, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { getUnixTime, parseISO } from "date-fns";
import { orderBy, take } from "lodash-es";
import { Link, useNavigate } from "react-router-dom";

import { Flow, FlowVersionFlowChild } from "src/api/flowTypes";
import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { Pill } from "src/base-components/Pill";
import { Icon } from "src/design-system/Icon";
import { useAuthoringContext } from "src/router/routerContextHooks";
import {
  AuthorPageParams,
  getFlowVersionsUrl,
  getUrlToAuthoringPage,
} from "src/router/urls";
import {
  getActiveVersions,
  VersionStatusDotIndicator,
} from "src/utils/flowVersion";
import { useParamsDecode } from "src/utils/useParamsDecode";

const VersionDisplay: React.FC<{
  version: FlowVersionFlowChild;
}> = ({ version }) => (
  <div className="flex items-center gap-1">
    <VersionStatusDotIndicator size="3xs" version={version} />
    <div className="font-inter-medium-13px text-gray-800">{version.name}</div>
  </div>
);

type VersionPickerProps = {
  flow: Flow;
};

const takeFiveVersions = (
  versions: FlowVersionFlowChild[],
  currentVersion: FlowVersionFlowChild | undefined,
): FlowVersionFlowChild[] => {
  const ordered = orderBy(
    getActiveVersions(versions),
    (version) => getUnixTime(parseISO(version.updated_at)),
    "desc",
  );

  const topFive = take(ordered, 5);

  if (
    currentVersion &&
    !topFive.some((version) => version.id === currentVersion.id)
  ) {
    // If no current version in the array, return the top 4 and the current version
    return [...take(topFive, 4), currentVersion];
  }

  return topFive;
};

export const VersionPicker: React.FC<VersionPickerProps> = () => {
  const navigate = useNavigate();
  const { orgId, wsId, flow_id, version_id } =
    useParamsDecode<AuthorPageParams>();
  const { flow, version: currentVersion } = useAuthoringContext();

  const versions = takeFiveVersions(flow.versions, currentVersion);

  return (
    <FixedPositionedDropdown
      buttonClassName="rounded-full border-none"
      dataLoc="current-version-picker"
      elements={versions.map((version) => ({
        key: version.id,
        value: version,
      }))}
      placement="bottom start"
      renderButtonValue={(version) => (
        <Pill
          dataLoc="current-version-picker-button"
          size="lg"
          variant="outlined-white"
        >
          {version && <VersionStatusDotIndicator version={version} />}
          <Pill.Text>
            {version ? version.name : "No version selected"}
          </Pill.Text>
          <Pill.Icon icon={faChevronDown} size="base" />
        </Pill>
      )}
      renderFooter={() => {
        if (flow.versions.length < 6) return;

        return (
          <div className="flex min-w-[18em] items-center justify-between gap-x-1 px-5 py-2">
            <Link
              className="text-indigo-500"
              to={getFlowVersionsUrl(orgId, wsId, flow_id)}
            >
              See all versions
            </Link>
          </div>
        );
      }}
      renderValue={({ value: version, selected }) => (
        <div className="flex min-w-[18em] items-center justify-between gap-x-1">
          <VersionDisplay version={version} />
          {selected && <Icon icon={faCheck} size="sm" variant="active" />}
        </div>
      )}
      selected={version_id}
      onSelect={(versionId) =>
        navigate(getUrlToAuthoringPage(orgId, wsId, flow_id, versionId))
      }
    />
  );
};
